import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, HeartHandshake, Info, PenLine, FileText, Users, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";

// Definiera typerna för slides och demenstyper
type Slide = {
  type: string;
  content: string;
  author?: string;
};

type DemensType = {
  id: string;
  title: string;
  slides: Slide[];
};

const OmDemens = () => {
  // State för att hålla reda på aktiv flik för varje sjukdomstyp
  const [activeType, setActiveType] = useState<string>("alzheimers");

  // Data för de olika sjukdomstyperna
  const demensTypes: DemensType[] = [
    {
      id: "alzheimers",
      title: "Alzheimers sjukdom",
      slides: [
        {
          type: "Om",
          content: "Den vanligaste formen av demens, som utgör ca 60-70% av alla fall. Alzheimers orsakas av att proteiner ansamlas i hjärnan och bildar plack och trådar som skadar nervcellerna. Sjukdomen börjar ofta med minnesproblem och utvecklas gradvis över många år med ytterligare symptom."
        },
        {
          type: "Vanliga symptom",
          content: "Försämrat närminne, svårigheter att lära sig nya saker, problem med orientering i tid och rum, språksvårigheter, samt svårigheter med problemlösning och abstrakt tänkande. I senare stadier kan även personligheten förändras och fysiska symtom som gångsvårigheter uppkomma."
        },
        {
          type: "Citat",
          content: "\"Det började med att jag glömde småsaker, som var jag lagt nycklarna. Men sedan började jag tappa tråden mitt i en mening och glömma vad jag skulle köpa i affären, trots att jag hade en lista. Det var då jag förstod att något var fel.\"",
          author: "Lisa, 67 år, tidig Alzheimers"
        }
      ]
    },
    {
      id: "vaskular",
      title: "Vaskulär demens",
      slides: [
        {
          type: "Om",
          content: "Den näst vanligaste formen, som utgör ca 20% av fallen. Orsakas av störningar i blodflödet till hjärnan, ofta till följd av små strokeanfall eller skador på de små blodkärlen i hjärnan. Symptomen kan variera beroende på vilka delar av hjärnan som skadats."
        },
        {
          type: "Vanliga symptom",
          content: "Svårigheter med planering och organisering, nedsatt förmåga att fatta beslut och lösa problem, långsammare tankegång, koncentrationssvårigheter, samt emotionell instabilitet. Symtomen kan uppstå plötsligt efter en stroke eller utvecklas gradvis med återkommande mindre infarkter."
        },
        {
          type: "Citat",
          content: "\"Efter min stroke förändrades allt. Vissa dagar känner jag mig helt klar i huvudet, men plötsligt kan jag bli förvirrad och ha svårt att fatta enkla beslut. Det känns som att ha en dimma i hjärnan som kommer och går.\"",
          author: "Anders, 72 år, vaskulär demens"
        }
      ]
    },
    {
      id: "lewy",
      title: "Lewykroppsdemens",
      slides: [
        {
          type: "Om",
          content: "Orsakas av ansamlingar av proteinet alfa-synuklein i hjärnan. Kännetecknas av visuella hallucinationer, uppmärksamhetsproblem och försämringar i rörelseförmågan. Drabbade kan också uppleva kraftiga svängningar i vakenhet och uppmärksamhet."
        },
        {
          type: "Vanliga symptom",
          content: "Visuella hallucinationer, parkinsonliknande symtom som stelhet och långsamma rörelser, kraftiga svängningar i vakenhet och uppmärksamhet, sömnstörningar med livliga drömmar, samt känslighet för neuroleptika (antipsykotiska läkemedel)."
        },
        {
          type: "Citat",
          content: "\"Det svåraste är att jag ser saker som inte finns där. Ibland står det främmande människor i mitt vardagsrum som bara försvinner när jag blundar. Jag vet att de inte är verkliga, men det känns så när jag ser dem.\"",
          author: "Karin, 69 år, lewykroppsdemens"
        }
      ]
    },
    {
      id: "frontotemporal",
      title: "Frontotemporal demens",
      slides: [
        {
          type: "Om",
          content: "Drabbar främst pannloben och tinningloberna. Startar ofta tidigare än andra demenssjukdomar, ibland redan i 40-50-årsåldern. Kännetecknas av beteendeförändringar, personlighetsförändringar och språksvårigheter snarare än minnesproblem i tidiga stadier."
        },
        {
          type: "Vanliga symptom",
          content: "Personlighetsförändringar med bristande impulskontroll, olämpligt socialt beteende, apati, tvångsmässigt beteende, och språkliga svårigheter som ordfattigdom eller svårigheter att förstå ords betydelse. Minnet är ofta relativt intakt i tidiga stadier."
        },
        {
          type: "Citat",
          content: "\"Min fru blev en helt annan person. Hon som alltid varit så noga med sitt uppförande började säga olämpliga saker och bry sig mindre om sitt utseende. Det tog lång tid innan vi förstod att det var en demenssjukdom.\"",
          author: "Bengt, make till person med frontotemporal demens"
        }
      ]
    }
  ];

  useEffect(() => {
    // Sätt document title för SEO
    document.title = "Om demens - Demenshjältar | Symtom, typer och hjälp";
    
    // Skapa meta-beskrivning
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Lär dig om olika demenssjukdomar, tidiga tecken, symptom och hur du kan stödja en närstående. Fakta, råd och resurser från Demenshjältar.');
  }, []);

  return (
    <div className="flex-grow">
      {/* Hero-sektion med bakgrundsbild */}
      <div className="relative h-[500px] bg-cover bg-center" style={{ backgroundImage: 'url("/images/om-demens/om-demens-art-10.webp")' }}>
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="container mx-auto h-full flex items-center justify-center relative z-10 px-4">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Om demens
            </h1>
            <p className="text-xl md:text-2xl text-white max-w-2xl mx-auto">
              Kunskap är första steget på vägen mot en enklare vardag
            </p>
          </div>
        </div>
      </div>

      {/* Statistikblock */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-minnestig-purple mb-4">180 000</h2>
              <p className="text-gray-700">
                personer i Sverige lever med demens
              </p>
            </div>
            
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-minnestig-purple mb-4">2x</h2>
              <p className="text-gray-700">
              fler personer väntas få demens till år 2050
              </p>
            </div>
            
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-minnestig-purple mb-4">50-70%</h2>
              <p className="text-gray-700">
              av fallen är Alzheimer 
              </p>
            </div>
            
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-16">
            
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-minnestig-purple mb-4">25 000</h2>
              <p className="text-gray-700">
                personer diagnostiseras med demens varje år
              </p>
            </div>
            
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-minnestig-purple mb-4">60%</h2>
              <p className="text-gray-700">
                personer med demens i Sverige bor hemma
              </p>
            </div>
            
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-minnestig-purple mb-4">81 miljarder</h2>
              <p className="text-gray-700">
                kostar samhället lika mycket som försvaret årligen (i SEK)
              </p>
            </div>
            
          </div>
        </div>
      </section>

      {/* Introduktion */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-8 items-center max-w-5xl mx-auto">
            <div className="md:w-2/5">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="/images/om-demens/om-demens-art-05.webp" 
                  alt="Illustration om demens" 
                  className="w-full h-auto"
                />
              </div>
            </div>
            <div className="md:w-3/5">
              <h2 className="text-3xl font-bold text-minnestig-teal mb-6">Vad är demens?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Demens är ett samlingsnamn för flera sjukdomar som påverkar hjärnans funktioner, framför allt minnet och andra kognitiva förmågor som tankeförmåga, tidsuppfattning och språk.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Sjukdomsförloppet ser olika ut för alla. Med rätt kunskap, stöd och verktyg kan livet med demens bli betydligt enklare för både den drabbade och deras närstående. 
              </p>
              <p className="text-lg text-gray-700">
                Det finns fortfarande hopp och så mycket fina minnen kvar att hämta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Olika typer av demenssjukdomar */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 md:px-2">
          <div className="max-w-4xl mx-auto md:max-w-6xl">
            <h2 className="text-3xl font-bold text-minnestig-teal mb-10 text-center">Olika typer av demenssjukdomar</h2>
            
            {/* Flikar för de olika sjukdomstyperna */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {demensTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setActiveType(type.id)}
                  className={`px-4 py-2 rounded-full transition-all ${
                    activeType === type.id
                      ? "bg-minnestig-purple text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {type.title}
                </button>
              ))}
            </div>
            
            {/* Innehållsområde med karusell */}
              {demensTypes.map((type) => (
                <div key={type.id} className={activeType === type.id ? "block" : "hidden"}>
                  {/* Desktop-vy: Visa alla kort bredvid varandra */}
                  <div className="hidden md:grid md:grid-cols-3 md:gap-4">
                    {type.slides.map((slide, index) => (
                      <div key={index} className="p-4 md:p-2">
                        <div className="bg-[#F8F2FF] rounded-lg shadow-sm p-6 md:p-4 min-h-[250px] flex flex-col">
                          <div className="flex items-center mb-4">
                            {slide.type === "Om" && <Info className="text-minnestig-purple mr-2" size={20} />}
                            {slide.type === "Vanliga symptom" && <Brain className="text-minnestig-purple mr-2" size={20} />}
                            {slide.type === "Citat" && <Quote className="text-minnestig-purple mr-2" size={20} />}
                            <h4 className="text-xl font-semibold text-minnestig-teal">{slide.type}</h4>
                          </div>
                          
                          <div className="flex-grow">
                            {slide.type === "Citat" ? (
                              <>
                                <blockquote className="italic text-gray-700 mb-4">
                                  {slide.content}
                                </blockquote>
                                {slide.author && (
                                  <p className="text-right text-gray-600">— {slide.author}</p>
                                )}
                              </>
                            ) : (
                              <p className="text-gray-700">{slide.content}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Mobil-vy: Behåll karusellen men med större pilar och mindre avstånd */}
                  <Carousel className="md:hidden w-full">
                    <CarouselContent>
                      {type.slides.map((slide, index) => (
                        <CarouselItem key={index}>
                          <div className="p-0">
                            <div className="bg-[#F8F2FF] rounded-lg shadow-sm p-6 min-h-[250px] flex flex-col">
                              <div className="flex items-center mb-4">
                                {slide.type === "Om" && <Info className="text-minnestig-purple mr-2" size={20} />}
                                {slide.type === "Vanliga symptom" && <Brain className="text-minnestig-purple mr-2" size={20} />}
                                {slide.type === "Citat" && <Quote className="text-minnestig-purple mr-2" size={20} />}
                                <h4 className="text-xl font-semibold text-minnestig-teal">{slide.type}</h4>
                              </div>
                              
                              <div className="flex-grow">
                                {slide.type === "Citat" ? (
                                  <>
                                    <blockquote className="italic text-gray-700 mb-4">
                                      {slide.content}
                                    </blockquote>
                                    {slide.author && (
                                      <p className="text-right text-gray-600">— {slide.author}</p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-gray-700">{slide.content}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <div className="flex justify-center mt-2 gap-4">
                      <CarouselPrevious className="static translate-y-0 mx-0 h-10 w-10" />
                      <CarouselNext className="static translate-y-0 mx-0 h-10 w-10" />
                    </div>
                  </Carousel>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Tidiga tecken och symptom */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-minnestig-teal mb-6 text-center">Vanliga tecken och symptom</h2>
            
            <p className="text-lg text-gray-700 mb-10 text-center">
              Demens drabbar varje individ olika, men vanliga symtom är:
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="mr-4">
                  <div className="rounded-full bg-minnestig-lavender/30 w-12 h-12 flex items-center justify-center">
                    <Brain className="text-minnestig-purple" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Minnesproblem</h3>
                  <p className="text-gray-700">
                    Svårigheter att minnas nya saker, glömma viktiga datum eller händelser, upprepa samma frågor och ökat behov av minneshjälpmedel.
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="mr-4">
                  <div className="rounded-full bg-minnestig-peach/30 w-12 h-12 flex items-center justify-center">
                    <PenLine className="text-minnestig-purple" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Språksvårigheter</h3>
                  <p className="text-gray-700">
                    Problem att följa med i eller delta i samtal, svårt att hitta rätt ord, benämna föremål eller förstå skriftliga instruktioner.
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="mr-4">
                  <div className="rounded-full bg-minnestig-lavender/30 w-12 h-12 flex items-center justify-center">
                    <Info className="text-minnestig-purple" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Förvirring</h3>
                  <p className="text-gray-700">
                    Svårigheter med tidsuppfattning, att navigera till välbekanta platser eller att känna igen tidigare välkända människor.
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="mr-4">
                  <div className="rounded-full bg-minnestig-peach/30 w-12 h-12 flex items-center justify-center">
                    <FileText className="text-minnestig-purple" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Planeringssvårigheter</h3>
                  <p className="text-gray-700">
                    Problem med att planera, följa recept, hantera ekonomi eller fatta rationella beslut.
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="mr-4">
                  <div className="rounded-full bg-minnestig-lavender/30 w-12 h-12 flex items-center justify-center">
                    <Users className="text-minnestig-purple" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Beteendeförändringar</h3>
                  <p className="text-gray-700">
                    Personlighetsförändringar, tillbakadragenhet från sociala aktiviteter, depression, ångest, agitation eller apati.
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="mr-4">
                  <div className="rounded-full bg-minnestig-peach/30 w-12 h-12 flex items-center justify-center">
                    <HeartHandshake className="text-minnestig-purple" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Minskad initiativförmåga</h3>
                  <p className="text-gray-700">
                    Passivitet, minskat intresse för hobbyer och aktiviteter eller behov av uppmuntran för att delta i sociala sammanhang.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Behandla demens */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-minnestig-teal mb-8 text-center">Behandla demens</h2>
            
            <div className="mb-12">
              <p className="text-lg text-gray-700 mb-6">
                Det finns idag inget botemedel mot de flesta demenssjukdomar, men det finns läkemedel och andra behandlingsformer som kan bromsa sjukdomsförloppet, lindra symptom och förbättra livskvaliteten för både den drabbade och anhöriga.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Behandling av demens är oftast multimodal, vilket betyder att flera olika behandlingsformer kombineras för bästa resultat. Detta kan inkludera läkemedel, kognitiv stimulering, fysisk aktivitet och anpassningar i vardagen.
              </p>
            </div>
            
            {/* Fingerstudien */}
            <div className="flex flex-col md:flex-row items-start gap-8 mb-12">
              <div className="md:w-1/3">
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="/images/om-demens/om-demens-art-06.webp" 
                    alt="Illustration av FINGER-studien" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              <div className="md:w-2/3 bg-white p-8 pt-0 rounded-lg shadow-sm">
                <h3 className="text-2xl font-semibold text-minnestig-purple mb-4">Hälsosamma vanor</h3>
                <p className="text-lg text-gray-700 mb-4">
                  FINGER-studien är en banbrytande forskning som visar att en kombination av livsstilsförändringar kan förebygga kognitiv försämring hos äldre personer med förhöjd risk för demens.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                  Studien visar att följande faktorer kan ha en skyddande effekt mot demens:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Regelbunden fysisk aktivitet</li>
                  <li>Hälsosam kost (t.ex. medelhavskost)</li>
                  <li>Hjärngymnastik (t.ex. korsord eller snickeriprojekt)</li>
                  <li>Social aktivitet</li>
                  <li>Kontroll av kardiovaskulära riskfaktorer (blodtryck, kolesterol)</li>
                </ul>
                <p className="text-lg text-gray-700">
                  Resultaten från FINGER-studien har inspirerat liknande studier världen över och visar att livsstilsförändringar kan vara ett kraftfullt verktyg för att förebygga och bromsa utvecklingen av demens.
                </p>
              </div>
            </div>
            
            {/* Läkemedelsbehandling */}
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="md:w-1/3">
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="/images/om-demens/om-demens-art-07.webp" 
                    alt="Illustration av läkemedelsbehandling" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              <div className="md:w-2/3 bg-white p-8 pt-0 rounded-lg shadow-sm">
                <h3 className="text-2xl font-semibold text-minnestig-purple mb-4">Läkemedelsbehandling</h3>
                <p className="text-lg text-gray-700 mb-4">
                  Det finns flera typer av läkemedel som används vid behandling av demens:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li><strong>Kolinesterashämmare</strong> (donepezil, rivastigmin, galantamin) – Används främst vid Alzheimers sjukdom för att förbättra kognitiva funktioner.</li>
                  <li><strong>NMDA-receptorantagonister</strong> (memantin) – Kan hjälpa till att skydda hjärnceller mot skadliga effekter av överaktivitet.</li>
                  <li><strong>Symptomlindrande läkemedel</strong> – För att hantera beteendemässiga och psykiska symptom som ångest, agitation och sömnstörningar.</li>
                </ul>
                <p className="text-lg text-gray-700">
                  Läkemedelsbehandling måste alltid anpassas individuellt och sker i samråd med läkare. Effekten varierar från person till person och regelbunden uppföljning är viktig.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Starta en demensutredning */}
      <section className="py-16 bg-[#F8F2FF]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-minnestig-teal mb-6 text-center">Starta en demensutredning</h2>
            
            <p className="text-lg text-gray-700 mb-10 text-center">
              Tidig diagnos ger bättre möjligheter till behandling och planering för framtiden.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Kolumn 1: Utredning online */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold text-minnestig-teal mb-4">Utredning online</h3>
                <p className="text-gray-700 mb-6">
                  Nu kan du göra en första bedömning av minnesproblem online. Det är enkelt, säkert och kan hjälpa dig att ta nästa steg.
                </p>
                <div className="flex flex-col space-y-3">
                  <a 
                    href="https://minnesmottagningen.se" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-minnestig-purple hover:text-minnestig-teal inline-flex items-center"
                  >
                    <span className="mr-2">Boka tid</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  
                  <a 
                    href="https://www.minnesmottagningen.se/#snabbtest" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-minnestig-purple hover:text-minnestig-teal inline-flex items-center"
                  >
                    <span className="mr-2">Gör ett snabbtest</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
              
              {/* Kolumn 2: Boka tid på vårdcentralen */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold text-minnestig-teal mb-4">Boka tid på vårdcentralen</h3>
                <p className="text-gray-700 mb-4">
                  Vårdcentralen är oftast första steget vid misstanke om demens. Läkaren gör en inledande bedömning och kan remittera vidare till specialister vid behov.
                </p>
                <ul className="list-disc pl-5 mb-6 text-gray-700 space-y-2">
                  <li>Kontakta din vårdcentral för en tid</li>
                  <li>Berätta om dina eller din anhörigs symptom</li>
                  <li>Ta gärna med en närstående till besöket</li>
                </ul>
              </div>
            </div>
            
            {/* Ny rad: Hur går utredningen till - horisontell tidslinje */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-minnestig-purple mb-6 text-center">Hur går utredningen till?</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="bg-minnestig-purple w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-white">1</span>
                  </div>
                  <h4 className="font-semibold text-minnestig-teal mb-2">Inledande samtal</h4>
                  <p className="text-sm text-gray-700">
                    Läkaren ställer frågor om symptom, när de började och hur de påverkar vardagen.
                  </p>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="bg-minnestig-purple w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-white">2</span>
                  </div>
                  <h4 className="font-semibold text-minnestig-teal mb-2">Kognitiva tester</h4>
                  <p className="text-sm text-gray-700">
                    Enkla tester för att bedöma minne, språk, uppmärksamhet och andra kognitiva funktioner.
                  </p>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="bg-minnestig-purple w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-white">3</span>
                  </div>
                  <h4 className="font-semibold text-minnestig-teal mb-2">Medicinska undersökningar</h4>
                  <p className="text-sm text-gray-700">
                    Blodprover, blodtryck och eventuellt EKG för att utesluta andra orsaker.
                  </p>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="bg-minnestig-purple w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-white">4</span>
                  </div>
                  <h4 className="font-semibold text-minnestig-teal mb-2">Hjärnavbildning</h4>
                  <p className="text-sm text-gray-700">
                    Datortomografi (CT) eller magnetröntgen (MR) för att undersöka hjärnans struktur.
                  </p>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="bg-minnestig-purple w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-white">5</span>
                  </div>
                  <h4 className="font-semibold text-minnestig-teal mb-2">Uppföljning och diagnos</h4>
                  <p className="text-sm text-gray-700">
                    Resultaten sammanställs och läkaren diskuterar diagnos och behandlingsalternativ.
                  </p>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Vanliga myter om demens */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-minnestig-teal mb-10 text-center">Vanliga myter om demens</h2>
            
            {/* Myt 1 */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 rounded-full bg-[#990033] flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center">
                      <h3 className="text-xl font-semibold text-gray-700">
                        "Demens är en naturlig del av åldrandet."
                      </h3>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 rounded-full bg-[#00CC66] flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-gray-700">
                        Demens är en sjukdom. Att bli glömsk ibland är normalt, men demens är inte en naturlig del av åldrandet, utan orsakas av skador på hjärnan.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Myt 2 */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 rounded-full bg-[#990033] flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center">
                      <h3 className="text-xl font-semibold text-gray-700">
                        "Alla med demens glömmer på samma sätt."
                      </h3>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 rounded-full bg-[#00CC66] flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-gray-700">
                        Sjukdomen yttrar sig olika. Symptomen varierar mellan olika demenssjukdomar och från person till person. Varje fall är unikt.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Myt 3 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 rounded-full bg-[#990033] flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center">
                      <h3 className="text-xl font-semibold text-gray-700">
                        "Det går inte att göra något."
                      </h3>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 rounded-full bg-[#00CC66] flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-gray-700">
                        Med rätt stöd kan livskvaliteten bli hög. Det finns behandlingar, aktiviteter och anpassningar som kan hjälpa både den drabbade och anhöriga.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* CTA-sektion */}
      <section className="py-16 bg-minnestig-purple/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-minnestig-purple mb-6">Vi finns här för dig</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Hos Demenshjältar hittar du gemenskap, kunskap och verktyg för att hantera livet med demens, oavsett om du själv är drabbad eller är anhörig.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/community" className="px-6 py-3 bg-minnestig-purple text-white font-medium rounded-lg hover:bg-minnestig-purple/90 transition-colors">
              Gå med i communityn
            </Link>
            <Link to="/kontakt" className="px-6 py-3 bg-white text-minnestig-teal font-medium rounded-lg border border-minnestig-teal hover:bg-minnestig-teal/10 transition-colors">
              Kontakta oss
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OmDemens; 